import classNames from 'classnames';
import { NextRouter, useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import LazyLoad from 'react-lazyload';
import { sendAdSchoolClickEvent } from 'src/helpers/analyticsEvents';
import { preparePath } from 'src/helpers/seo';
import { createCursHasOffersParams } from 'src/utils/analytics';

import { Avatar, Button, Space, Typography } from '@sravni/react-design-system';
import { StarFilled } from '@sravni/react-icons';
import { useIsMobile } from '@sravni/react-utils';
import pluralize from '@sravni/utils/lib/pluralize';

import { IItem } from '../../interfaces/organizations';
import { loadEducationProductsList } from '../../services/products';

import styles from './OrganizationLinkBlock.module.scss';

interface TProps {
  organization: IItem;
  className?: string;
  rightSideClassName?: string;
}

const createLink = (link: string, org: IItem, router: NextRouter) => {
  const { asPath, query } = router;
  const path = preparePath(asPath);
  const urlClick = new URL(link);
  const urlParams = createCursHasOffersParams('reviews', org, query, path);

  Object.entries(urlParams).forEach(([key, value]) => {
    if (value) {
      urlClick.searchParams.set(key, value.toString());
    }
  });

  const utmSearchParams = new URLSearchParams(org.ad.utmString);
  const website = new URL(org.contacts.website);
  website.search = utmSearchParams.toString();

  urlClick.searchParams.set('url', website.toString());

  return urlClick.toString();
};

export const OrganizationLinkBlock: React.FC<TProps> = ({ organization, className, rightSideClassName }) => {
  const router = useRouter();
  const { rating = [], position } = organization;
  const ratingPerMonth = rating.find((el) => !el.fullPeriod);

  const isMobile = useIsMobile();
  const buttonWrapperRef = useRef<HTMLDivElement>();

  const [coursesCount, setCoursesCount] = useState<number | null>(null);

  const fetchCoursesCount = async () => {
    try {
      const data = await loadEducationProductsList({
        status: 'active',
        organizations: [organization.id],
        limit: 1,
        fields: ['id'],
      });
      if (data?.totalCount) {
        setCoursesCount(data.totalCount);
      }
    } catch (error) {}
  };

  const handleScroll = () => {
    if (!buttonWrapperRef.current) return null;

    if (window.scrollY >= 150) {
      buttonWrapperRef.current.classList.add(styles['container-scroll']);
    } else {
      buttonWrapperRef.current.classList.remove(styles['container-scroll']);
    }
  };

  useEffect(() => {
    if (isMobile) {
      window.addEventListener('scroll', handleScroll);
    } else if (!isMobile && buttonWrapperRef.current) {
      window.removeEventListener('scroll', handleScroll);
      buttonWrapperRef.current.classList.remove(styles['container-scroll']);
    }

    return () => window.removeEventListener('scroll', handleScroll);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [isMobile]);

  useEffect(() => {
    fetchCoursesCount();
  }, []);

  if (!organization.ad) {
    return null;
  }

  return (
    <div className={styles.container} ref={buttonWrapperRef}>
      <div className={classNames(className, styles.content)}>
        <Avatar src={organization.squareLogo} size={36} />
        <div className={styles['org-info']}>
          <Typography.Text size={16} strong>
            {organization.name}
          </Typography.Text>
          <Typography.Text size={12}>
            <StarFilled className={styles.star} /> <span>{position?.complexCalculatedRatingValue.toFixed(2)}</span>{' '}
            <span className={styles['gray-text']}>
              {ratingPerMonth?.approvedCount} {pluralize(ratingPerMonth?.approvedCount, 'оценка', 'оценки', 'оценок')}
            </span>
          </Typography.Text>
        </div>

        {coursesCount > 0 && (
          <div>
            <Typography.Text size={12} className={styles['gray-text']}>
              Кол-во курсов
            </Typography.Text>
            <Typography.Text size={16} strong>
              {coursesCount}
            </Typography.Text>
          </div>
        )}
        <Button
          variant="primary"
          className={styles.button}
          onClick={() => {
            sendAdSchoolClickEvent(organization);
            window.open(createLink(organization.ad.clickLink, organization, router));
          }}
        >
          {organization.ad.buttonText || 'Перейти на сайт'}
        </Button>
      </div>
      <LazyLoad>
        <img
          className={styles.pixel}
          src={createLink(organization.ad?.displayLink, organization, router)}
          alt="pixel"
        />
      </LazyLoad>
      <Space className={classNames(rightSideClassName, styles['right-side'])} direction={'vertical'} size={24} />
    </div>
  );
};
